import * as Sentry from '@sentry/react'

/**
 * Environment detection helper
 */
const getEnvironment = (): string => {
  const env = import.meta.env.VITE_SENTRY_ENVIRONMENT
  if (env) return env

  // Fallback: detect from hostname or mode
  if (import.meta.env.DEV) return 'development'
  if (import.meta.env.PROD) return 'production'

  return 'unknown'
}

/**
 * Determines if Sentry should be enabled
 * Only enable when DSN is provided
 */
const isSentryEnabled = (): boolean => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = getEnvironment()

  // Don't initialize if no DSN is provided
  if (!dsn || dsn.trim() === '') {
    console.warn('[Sentry] No DSN provided, Sentry will not be initialized')
    return false
  }

  // Log in development
  if (environment === 'development') {
    console.info('[Sentry] Development mode detected, Sentry is enabled for testing')
    return true
  }

  return true
}

/**
 * Get trace propagation targets based on environment
 * This ensures distributed tracing works with your backend
 */
const getTracePropagationTargets = (): (string | RegExp)[] => {
  const backendUrl = import.meta.env.VITE_MARKETPLACE_BACKEND_URL

  if (!backendUrl) {
    return ['localhost', /^\//]
  }

  try {
    const url = new URL(backendUrl)
    return [
      url.origin,
      /^\//, // Relative URLs
      'localhost',
      /^https?:\/\/.*\.myjobglasses\.(com|io)/, // MJG domains
    ]
  } catch {
    return ['localhost', /^\//]
  }
}

/**
 * Get sample rates based on environment
 */
const getSampleRates = () => {
  const environment = getEnvironment()

  if (environment === 'development') {
    return {
      tracesSampleRate: 1.0, // 100% in dev for debugging
      replaysSessionSampleRate: 0.1, // 10% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% when errors occur
    }
  }

  if (environment === 'staging') {
    return {
      tracesSampleRate: 0.5, // 50% in staging
      replaysSessionSampleRate: 0.2, // 20% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% when errors occur
    }
  }

  // Production
  return {
    tracesSampleRate: 0.1, // 10% in production (cost control)
    replaysSessionSampleRate: 0.05, // 5% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% when errors occur
  }
}

/**
 * Initialize Sentry SDK
 * Call this BEFORE React renders
 */
export const initializeSentry = (): void => {
  if (!isSentryEnabled()) {
    return
  }

  const environment = getEnvironment()
  const sampleRates = getSampleRates()

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // Environment and release info
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || `mjg-visio@${import.meta.env.VITE_APP_VERSION || 'unknown'}`,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track navigation performance
        enableInp: true,
      }),

      // Session replay for debugging
      Sentry.replayIntegration({
        // Mask all text and user input by default for privacy
        maskAllText: true,
        maskAllInputs: true,

        // Block media elements (images, videos) to reduce payload
        blockAllMedia: true,

        // Network details for API debugging
        networkDetailAllowUrls: getTracePropagationTargets(),
      }),
    ],

    // Trace propagation to backend (top-level option)
    tracePropagationTargets: getTracePropagationTargets(),

    // Performance monitoring sample rates
    tracesSampleRate: sampleRates.tracesSampleRate,

    // Session replay sample rates
    replaysSessionSampleRate: sampleRates.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sampleRates.replaysOnErrorSampleRate,

    // Privacy: beforeSend hook to scrub sensitive data
    beforeSend(event, hint) {
      // Remove sensitive data from error context
      if (event.request) {
        // Remove Authorization headers
        if (event.request.headers) {
          delete event.request.headers.Authorization
          delete event.request.headers.Cookie
        }

        // Remove sensitive query params
        if (event.request.url) {
          try {
            const url = new URL(event.request.url)
            url.searchParams.delete('token')
            url.searchParams.delete('password')
            event.request.url = url.toString()
          } catch {
            // Invalid URL, skip
          }
        }
      }

      // Filter out known non-critical errors
      const error = hint.originalException
      if (error instanceof Error) {
        // Ignore ResizeObserver errors (browser quirk)
        if (error.message.includes('ResizeObserver')) {
          return null
        }

        // Ignore Chime SDK expected errors (optional)
        if (error.message.includes('amazon-chime-sdk')) {
          // Log to console but don't send to Sentry
          console.warn('[Chime SDK Warning]', error)
          return null
        }
      }

      return event
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',

      // Network errors (handled by app)
      'NetworkError',
      'Network request failed',

      // Expected user actions
      'AbortError',
      'cancelled',
    ],

    // Enable debug mode in development
    debug: environment === 'development',

    // Breadcrumb filters
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console logs in production
      if (breadcrumb.category === 'console' && environment === 'production') {
        return null
      }
      return breadcrumb
    },
  })

  console.info(`[Sentry] Initialized for environment: ${environment}`)
}

// Export Sentry namespace for manual error reporting
export { Sentry }
